/**
 * [MVP Observability] FE telemetry SDK tối giản.
 * Bắt: JS error, unhandled promise, LCP, page load, page_view.
 * Gom batch -> POST tới Telemetry API (license-manager /api/telemetry).
 * Offline IndexedDB queue để "làm sau" (techspec); MVP dùng in-memory + sendBeacon lúc unload.
 */

interface TelemetryItem {
  telemetryType: string
  eventType?: string
  url?: string
  browser?: string
  durationMs?: number
  value?: number
  errorMessage?: string
  stackTrace?: string
  properties?: any
}

const QUEUE: TelemetryItem[] = []
let endpoint = ''
const BATCH_SIZE = 20
const FLUSH_MS = 5000

const enqueue = (item: TelemetryItem) => {
  QUEUE.push({ browser: navigator.userAgent, url: location.pathname, ...item })
  if (QUEUE.length >= BATCH_SIZE) flush()
}

const flush = () => {
  if (!QUEUE.length || !endpoint) return
  const events = QUEUE.splice(0, QUEUE.length)
  fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ events }),
    keepalive: true,
  }).catch(() => {
    /* drop khi lỗi mạng — MVP không retry; offline queue là việc sau */
  })
}

const flushBeacon = () => {
  if (!QUEUE.length || !endpoint) return
  const events = QUEUE.splice(0, QUEUE.length)
  try {
    navigator.sendBeacon(endpoint, new Blob([JSON.stringify({ events })], { type: 'application/json' }))
  } catch {
    /* ignore */
  }
}

export const Telemetry = {
  init() {
    // Đăng ký listener NGAY (kể cả trước khi có endpoint — event vào buffer).
    window.addEventListener('error', e =>
      enqueue({
        telemetryType: 'js_error',
        errorMessage: e.message,
        stackTrace: (e.error && e.error.stack) || undefined,
      }),
    )
    window.addEventListener('unhandledrejection', e =>
      enqueue({
        telemetryType: 'promise_error',
        errorMessage: String((e.reason && e.reason.message) || e.reason),
        stackTrace: e.reason && e.reason.stack,
      }),
    )

    // Web vitals: LCP (Largest Contentful Paint)
    try {
      new PerformanceObserver(list => {
        const entries = list.getEntries()
        const last: any = entries[entries.length - 1]
        enqueue({ telemetryType: 'lcp', value: Math.round(last.startTime) })
      }).observe({ type: 'largest-contentful-paint', buffered: true })
    } catch {
      /* trình duyệt không hỗ trợ */
    }

    // Page load timing
    window.addEventListener('load', () => {
      const nav: any = performance.getEntriesByType('navigation')[0]
      if (nav) enqueue({ telemetryType: 'page_load', durationMs: Math.round(nav.loadEventEnd - nav.startTime) })
    })

    // Flush định kỳ + lúc rời trang
    setInterval(flush, FLUSH_MS)
    window.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') flushBeacon()
    })

    // Lấy endpoint từ process.env.LICENSE_MANAGER_URL (webpack DefinePlugin thay -> string literal
    // lúc build, KHÔNG truy cập `process` runtime). Bọc try/catch phòng môi trường không được
    // DefinePlugin xử lý (process undefined) -> telemetry im lặng thay vì crash app.
    try {
      const base = (process.env.LICENSE_MANAGER_URL as string) || ''
      if (base) endpoint = `${new URL(base).origin}/api/telemetry`
    } catch {
      /* process/URL không khả dụng -> telemetry im lặng */
    }
  },

  pageView(path: string) {
    enqueue({ telemetryType: 'page_view', url: path })
  },

  error(err: any) {
    enqueue({
      telemetryType: 'js_error',
      errorMessage: (err && err.message) || String(err),
      stackTrace: err && err.stack,
    })
  },
}
